import { usePortalMessage } from "../../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
/**
 * `data-screen-label="Agent"`, tab `channels` — **When it answers** and the overflow card.
 *
 * WHERE THESE VALUES LIVE. Opening hours, closed dates, the out-of-hours behaviour and the overflow
 * behaviour are part of `agent_version.config_document`, which ADR 0002 and design §4.6 make
 * **immutable** — an `agent_version` is what a conversation is admitted onto and pinned to for its whole
 * life. So nothing here changes the version that is answering.
 *
 * AMENDED 2026-09-25 (customer-portal review #115, #76): read-only values were drawn as text rather
 * than as boxes that look editable and save nothing. `00:00–00:00` and `00:00–23:59` read "Open all
 * day". "Ask for more lines" opened the concurrency request (`QuotaRequestDialog`).
 *
 * AMENDED 2026-10-09 (design sync, the Claude Design export of 2026-10-09). The design draws an hours
 * EDITOR: each day Open or Closed, its periods as HH:MM boxes, "Add hours", "Copy to all open days"
 * with Undo, and special dates as chips. The opening hours are now edited here for real: they are the
 * template's `opening_hours` answer, so "Save hours" sends the agent's current answers with the new
 * week through the same edit the Versions tab's Edit uses (`editVoiceAgent`). That builds a NEW
 * version; the live one keeps answering until it is published on Versions, and the save line says so.
 *
 * AMENDED 2026-10-09 (plan-015 AGC-UI, gaps G3, G4, G8, G12; contract IC-DG-2). Everything on the card
 * is edited for real now — nothing is drawn disabled with "arrives soon": the agent's own time zone
 * ("Times are in <zone>, the agent's time zone · Change"), the special dates (chips with remove, the
 * add row), "Out of hours" (say you are closed / take a message / forward), "When every line is busy"
 * (queue / take a message / forward) and the longest wait (1, 2, 3 or 5 minutes). One save sends the
 * week and only the answering groups that changed through the same edit; a group left out keeps the
 * live version's. The queue's sentence is what callers really hear (owner answer 13), its minutes the
 * hold the live version gives (`busy_wait_second`) until the person picks another. "Forward to a
 * phone" is greyed with its reason while forwarding is not available (`forward_to_phone`, served on
 * `getVoiceAgent`) and states the ring, the WeTalk-numbers-only rule (owner answer 19) and the time
 * limit once it is. "Request more lines" opens the concurrency request; "See the N callers" opens
 * History.
 *
 * AMENDED 2026-10-10 (design sync of the export of 2026-10-10): the zone is named as the export names it — "Times are in
 * Rome time (Italy), the agent's time zone" (`timeZoneLabelOf`; the agent's own `time_zone`, its IANA name the
 * select's value) — and the special dates say "Days you're closed or open different hours.", Closed / Different hours.
 *
 * AMENDED 2026-10-14 (owner decision; ADR 0061 amendment, design §30.19): an agent whose engine answers from the owner's
 * information (`answersAtAllHoursOf`, the catalogue's engine capability — never the template code) answers questions
 * and takes messages at every hour; its opening hours and special dates decide only when a caller can be put through to
 * a person. Its dek says so, and the "Out of hours" card is not drawn — the runtime never plays an out-of-hours plan for
 * it. A stored out-of-hours "forward" reads as "take a message" (`allHoursChoiceOf`), so it neither draws the forward
 * number nor blocks a save; the busy card is unchanged. Every other agent's card is exactly as before.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Icon, Tag } from "@wetalk/ui";
import { calendarDateIn } from "@wetalk/domain";
import { useState, type ReactNode } from "react";

import { PendingQuotaRequestLine, QuotaRequestDialog } from "../../../billing/quota-request-dialog.js";
import { apiErrorText } from "../../../../api/api-error.js";
import { useGateway } from "../../../../api/provider.js";
import { QUERY_KEY, voiceAgentQuery } from "../../../../api/query.js";
import type { AnsweringRequestView } from "../../../../api/slice/voice-agent-edit.js";
import { GLYPH } from "../../../../shell/glyph.js";
import { useMemberCan } from "../../../../shell/member-capability.js";
import {
  DAY_NAME,
  DEFAULT_ANSWERING_CHOICE,
  DEFAULT_WAIT_MINUTE,
  MAX_PERIOD_PER_DAY,
  afterHoursOptionOf,
  allHoursChoiceOf,
  answeringChoiceProblemOf,
  answersAtAllHoursOf,
  busyOptionOf,
  forwardClosedReasonOf,
  forwardLimitSentenceOf,
  forwardNumberProblemOf,
  forwards,
  isWaitMinute,
  openingHoursText,
  openingWeekProblem,
  sameAnsweringChoice,
  sameSpecialDate,
  sentChoiceOf,
  timeZoneLabelOf,
  timeZoneOptionOf,
  waitNoteOf,
  type AfterHoursChoiceCode,
  type AnsweringChoice,
  type BusyChoiceCode,
  type OpeningPeriod,
  type SpecialDateEntry,
} from "../../new/opening-hours.js";
import {
  AnsweringChoiceGroup,
  ForwardNumberField,
  LongestWaitField,
  SpecialDateEditor,
} from "../../new/opening-hours-week.js";
import { openingHoursFillOf, openingHoursFillProblemOf, specialDateOfFill } from "../../new/opening-hours-fill.js";
import { useHelpFillable } from "../../../../help/fill/use-help-fillable.js";

import type { AnsweringScheduleView, ClosedDateView } from "../../../../api/gateway.js";

export interface AnsweringPanelProps {
  readonly voiceAgentId: string;
  readonly schedule: AnsweringScheduleView;
  /** Today's organization limit, when known — what "Request more lines" proposes to raise. */
  readonly currentCeiling: number | null;
  /** This agent's own lines, for the busy card's sentence. */
  readonly voiceAgentCeiling: number | null;
  /** "See the N callers": History. */
  readonly onSeeCallers: () => void;
}

/**
 * One opening interval as text (review #76). The runtime reads an interval that closes at or
 * before it opens as running past midnight, so `00:00–00:00` is the whole day; `00:00–23:59` is the
 * workaround owners use for the same thing. Both read "Open all day".
 */
export function intervalText(interval: { readonly opensAt: string; readonly closesAt: string }): string {
  if (interval.opensAt === "00:00" && (interval.closesAt === "00:00" || interval.closesAt === "23:59")) {
    return portalText("operations", "open_all_day_d60531da");
  }
  return `${interval.opensAt} – ${interval.closesAt}`;
}

/** A day's hours as one line: "Closed", "Open all day", or "12:00 – 15:00, 18:00 – 23:00". */
export function dayHoursText(interval: readonly { readonly opensAt: string; readonly closesAt: string }[]): string {
  if (interval.length === 0) return portalText("operations", "closed_88d86b77");
  return interval.map(intervalText).join(", ");
}

/** What happened to the callers who found every line busy, one clause per behaviour. */
const OVERFLOW_CLAUSE = new Map<string, (count: number) => string>([
  ["queued", (count) => portalText("operations", "value_value_held_in_a_queue_d82a5336", { v0: String(String(count)), v1: String(count === 1 ? portalText("operations", "was_3de521d3") : portalText("operations", "were_a315dee0")) })],
  ["message_taken", (count) => portalText("operations", "value_left_a_message_632df811", { v0: String(String(count)) })],
  ["transferred", (count) => portalText("operations", "value_value_forwarded_aa309d15", { v0: String(String(count)), v1: String(count === 1 ? portalText("operations", "was_3de521d3") : portalText("operations", "were_a315dee0")) })],
]);

function overflowSentence(tally: AnsweringScheduleView["overflowLast7Day"]): string {
  const clause = tally
    .filter((one) => one.occurrence > 0)
    .map((one) => OVERFLOW_CLAUSE.get(one.behaviour)?.(one.occurrence) ?? `${String(one.occurrence)} ${one.behaviour}`);
  if (clause.length === 0) return "";
  const last = clause.at(-1) ?? "";
  const text = clause.length === 1 ? last : portalText("operations", "value_and_value_dcf74a8c", { v0: String(clause.slice(0, -1).join(", ")), v1: String(last) });
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
}

/** The document's behaviour kinds as the choices (`closed_message` → closed …); `null` for one they do not name. */
const AFTER_HOURS_OF_KIND: Readonly<Record<string, AfterHoursChoiceCode>> = {
  closed_message: "closed",
  message_taken: "message",
  transferred: "forward",
};
const BUSY_OF_KIND: Readonly<Record<string, BusyChoiceCode>> = {
  queued: "queue",
  message_taken: "message",
  transferred: "forward",
};

/**
 * The live version's choices read from its published behaviours — for an API that does not serve them on
 * `getVoiceAgent` yet. `null` when a behaviour is not one the choices name; a queue whose wait is not one of the four
 * reads with no wait chosen (the note still says the wait callers are given).
 */
export function answeringChoiceOfSchedule(schedule: AnsweringScheduleView): AnsweringChoice | null {
  const afterHoursChoice = schedule.afterHours === null ? undefined : AFTER_HOURS_OF_KIND[schedule.afterHours.kind];
  const busyChoice = schedule.overflow === null ? undefined : BUSY_OF_KIND[schedule.overflow.kind];
  if (afterHoursChoice === undefined || busyChoice === undefined) return null;
  const waitMs = schedule.overflow?.maxWaitMs ?? null;
  const minute = waitMs === null ? null : waitMs / 60_000;
  const forwardTo = schedule.afterHours?.transferTo ?? schedule.overflow?.transferTo ?? null;
  return {
    afterHoursChoice,
    busyChoice,
    longestWaitMinute: busyChoice === "queue" && isWaitMinute(minute) ? minute : null,
    forwardTo: afterHoursChoice === "forward" || busyChoice === "forward" ? forwardTo : null,
  };
}

function specialDateOfSchedule(closed: readonly ClosedDateView[]): readonly SpecialDateEntry[] {
  return closed.map((one) => ({ date: one.date, label: one.label === one.date ? "" : one.label, interval: one.interval }));
}

const DAY_CODE = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
const DAY_SHORT = () => ([portalText("operations", "mon_24b2a099"), portalText("operations", "tue_529541bb"), portalText("operations", "wed_23408b19"), portalText("operations", "thu_3593ccd9"), portalText("operations", "fri_bbd6e32e"), portalText("operations", "sat_6b782d41"), portalText("operations", "sun_48c98cab")] as const);

/** One day in the editor: open or closed, and its periods (kept while closed, so a toggle restores them). */
interface EditedDay {
  readonly open: boolean;
  readonly period: readonly OpeningPeriod[];
}

function editedWeekOf(schedule: AnsweringScheduleView): readonly EditedDay[] {
  return DAY_CODE.map((code) => {
    const day = schedule.week.find((one) => one.day === code);
    const period = (day?.interval ?? []).map((one) => ({ opensAt: one.opensAt, closesAt: one.closesAt }));
    return { open: period.length > 0, period: period.length > 0 ? period : [{ opensAt: "12:00", closesAt: "15:00" }] };
  });
}

function weekOf(edited: readonly EditedDay[]): readonly (readonly OpeningPeriod[])[] {
  return edited.map((day) => (day.open ? day.period : []));
}

export function AnsweringPanel({
  voiceAgentId,
  schedule,
  currentCeiling,
  voiceAgentCeiling,
  onSeeCallers,
}: AnsweringPanelProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const canEdit = useMemberCan().can("voice_agent.write");
  // plan-015: the live answering answers and the forward facts, served on `getVoiceAgent` (the page's own query).
  const voiceAgent = useQuery(voiceAgentQuery(gateway, voiceAgentId));
  const detail = voiceAgent.data?.detail;
  const forwardFact = detail?.forward ?? null;
  const publishedAnswer = detail?.answering ?? null;
  const answersAtAllHours = answersAtAllHoursOf(voiceAgent.data?.agentTemplateCode ?? null);

  const [quotaRequestOpen, setQuotaRequestOpen] = useState(false);
  const [week, setWeek] = useState<readonly EditedDay[]>(() => editedWeekOf(schedule));
  const [copied, setCopied] = useState<{ readonly from: string; readonly count: number; readonly previous: readonly EditedDay[] } | null>(null);
  const [saved, setSaved] = usePortalMessage();
  // `null` until the person changes it: the live value applies.
  const [editedZone, setEditedZone] = useState<string | null>(null);
  const [changingZone, setChangingZone] = useState(false);
  const [editedDate, setEditedDate] = useState<readonly SpecialDateEntry[] | null>(null);
  const [editedChoice, setEditedChoice] = useState<AnsweringChoice | null>(null);
  // The instant the card opened: "today" for refusing a past special date, read once rather than on every render.
  const [openedAtMs] = useState(() => Date.now());

  const baseZone = publishedAnswer?.timeZone ?? schedule.configuredTimeZone ?? schedule.timeZone;
  const baseDate = publishedAnswer?.specialDate ?? specialDateOfSchedule(schedule.closedDate);
  const readChoice = publishedAnswer === null ? answeringChoiceOfSchedule(schedule) : publishedAnswer.choice;
  const baseChoice = answersAtAllHours && readChoice !== null ? allHoursChoiceOf(readChoice) : readChoice;
  const zone = editedZone ?? baseZone;
  const specialDate = editedDate ?? baseDate;
  const choice = editedChoice ?? baseChoice;

  const weekChanged = openingHoursText(weekOf(week)) !== openingHoursText(weekOf(editedWeekOf(schedule)));
  const zoneChanged = zone !== baseZone;
  const dateChanged = !sameSpecialDate(specialDate, baseDate);
  const choiceChanged = editedChoice !== null && !sameAnsweringChoice(editedChoice, baseChoice);
  const changed = weekChanged || zoneChanged || dateChanged || choiceChanged;
  const problem =
    (weekChanged ? openingWeekProblem(weekOf(week)) : null) ??
    (choiceChanged && choice !== null ? answeringChoiceProblemOf(choice, forwardFact) : null);
  const onlyWeek = weekChanged && !zoneChanged && !dateChanged && !choiceChanged;

  const save = useMutation({
    mutationFn: async () => {
      const answer = await gateway.getVoiceAgentAnswers(voiceAgentId, null);
      const code = Object.keys(answer.openingWeek)[0];
      if (code === undefined) throw new Error(portalText("operations", "this_agent_s_template_has_no_opening_hours_to_change_25e144af"));
      const answering: AnsweringRequestView = {
        ...(zoneChanged ? { timeZone: zone } : {}),
        ...(dateChanged ? { specialDate } : {}),
        ...(choiceChanged && choice !== null ? { choice: sentChoiceOf(choice) } : {}),
      };
      const made = await gateway.editVoiceAgent(voiceAgentId, {
        agentVersionId: null,
        expectedLiveVersionSeq: answer.liveVersionSeq,
        fieldValue: weekChanged ? { ...answer.fieldValue, [code]: openingHoursText(weekOf(week)) } : answer.fieldValue,
        languageCode: answer.languageCode,
        voiceByLanguage: answer.voiceByLanguage,
        greeting: answer.greeting,
        recordingEnabled: answer.recordingEnabled,
        paymentMethod: answer.paymentMethod,
        documents: "keep",
        answering,
      });
      return made.agentVersionLabel;
    },
    onSuccess: async (label) => {
      setSaved(
        () => (portalText("operations", "value_is_building_with_these_value_the_live_version_keeps_5b52727d", { v0: String(label), v1: String(onlyWeek ? portalText("operations", "hours_5aedee09") : portalText("operations", "changes_49a04ba4")), v2: String(label) })),
      );
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
    },
  });

  // Owner request 2026-10-18: the help assistant may propose ONE opening-hours change set for this card — days, zone,
  // special dates — applied through the card's own state below; "Save hours" (which builds a new version) stays the
  // person's button. Two shifts a day here, as this editor draws.
  const helpToday = calendarDateIn(zone, openedAtMs);
  const helpFill = useHelpFillable("agent_opening_hours", {
    opening_hours:
      !schedule.published || !canEdit
        ? undefined
        : {
            value: openingHoursFillOf(weekOf(week), zone, specialDate),
            set: (next) => {
              setCopied(null);
              setSaved(null);
              setWeek((held) =>
                held.map((day, index) => {
                  const shift = next.day?.[String(index + 1) as "1"];
                  if (shift === undefined) return day;
                  if (shift.length === 0) return { ...day, open: false };
                  return {
                    open: true,
                    period: shift.map((one) => {
                      const [opensAt = "", closesAt = ""] = one === "00:00-24:00" ? ["00:00", "00:00"] : one.split("-");
                      return { opensAt, closesAt };
                    }),
                  };
                }),
              );
              if (next.time_zone !== undefined && next.time_zone !== zone) setEditedZone(next.time_zone);
              const dates = specialDateOfFill(next);
              if (dates !== null) setEditedDate(dates);
            },
            accepts: (next) => openingHoursFillProblemOf(next, { timeZone: zone, specialDate, today: helpToday, maxShift: MAX_PERIOD_PER_DAY }) === null,
          },
  });

  if (!schedule.published) {
    return (
      <>
        <h2 className="ch-section-title">{portalText("operations", "when_it_answers_c31a7ec2")}</h2>
        <div className="ch-card">
          <p className="ch-card-label">{portalText("operations", "nothing_is_published_yet_99bf00e4")}</p>
          <p className="ch-card-foot">
            {portalText("operations", "opening_hours_closed_dates_and_what_happens_out_of_hours_a_db50e797")}</p>
        </div>
      </>
    );
  }

  const busyCount = schedule.overflowLast7Day.reduce((total, one) => total + one.occurrence, 0);
  const touch = (): void => {
    setCopied(null);
    setSaved(null);
  };
  const setDay = (index: number, change: (day: EditedDay) => EditedDay): void => {
    touch();
    setWeek((held) => held.map((day, at) => (at === index ? change(day) : day)));
  };
  const choose = (next: Partial<AnsweringChoice>): void => {
    touch();
    setEditedChoice({ ...(choice ?? DEFAULT_ANSWERING_CHOICE), ...next });
  };
  const discard = (): void => {
    setWeek(editedWeekOf(schedule));
    setCopied(null);
    setEditedZone(null);
    setChangingZone(false);
    setEditedDate(null);
    setEditedChoice(null);
  };
  const firstOpen = week.findIndex((day) => day.open);
  const closedReason = forwardClosedReasonOf(forwardFact);
  const queued = choice?.busyChoice === "queue";
  const waitMinute = queued ? (choice.longestWaitMinute ?? null) : null;
  const publishedWaitMs = schedule.overflow?.kind === "queued" ? schedule.overflow.maxWaitMs : null;
  // G8: the hold callers are really given (served, else the published behaviour's), until the person picks another.
  const servedWaitSecond = publishedAnswer?.busyWaitSecond ?? (publishedWaitMs === null ? null : Math.round(publishedWaitMs / 1000));
  const waitSecond = !choiceChanged && servedWaitSecond !== null ? servedWaitSecond : (waitMinute ?? DEFAULT_WAIT_MINUTE) * 60;
  const forwardProblem =
    choiceChanged && choice !== null && forwards(choice) && closedReason === null ? forwardNumberProblemOf(choice.forwardTo ?? "") : null;
  const today = calendarDateIn(zone, openedAtMs);

  return (
    <>
      <h2 className="ch-section-title">{portalText("operations", "when_it_answers_c31a7ec2")}</h2>
      <p className="ch-section-dek">
        {answersAtAllHours
          ? portalText("operations", "answering.all_hours_dek")
          : portalText("operations", "outside_these_hours_the_agent_still_picks_up_it_just_behav_c8aa3781")}</p>

      <div className="ch-answering">
        <div className="ch-card" {...helpFill.mark("opening_hours")}>
          <h3 className="ch-card-heading">{portalText("operations", "opening_hours_6117e118")}</h3>
          <div className="ch-hours">
            <p className="ch-hours-zone">
              <span>{portalText("operations", "times_are_in_ba8416d1")}{timeZoneLabelOf(zone)}{portalText("operations", "the_agent_s_time_zone_4256a594")}</span>

              {canEdit ? (
                <a
                  href="#"
                  aria-expanded={changingZone}
                  onClick={(event) => {
                    event.preventDefault();
                    setChangingZone((now) => !now);
                  }}
                >
                  {portalText("operations", "change_64fbd995")}</a>
              ) : null}
            </p>
            {changingZone ? (
              <span className="field c-answer-zone">
                <label htmlFor="answering-zone">{portalText("operations", "time_zone_eea79afd")}</label>
                <span className="c-answer-select">
                  <select
                    id="answering-zone"
                    className="input"
                    value={zone}
                    onChange={(event) => {
                      touch();
                      setEditedZone(event.currentTarget.value);
                    }}
                  >
                    {timeZoneOptionOf(zone).map((one) => (
                      <option key={one} value={one}>
                        {timeZoneLabelOf(one)}
                      </option>
                    ))}
                  </select>
                  <Icon glyph={GLYPH.caretDown} size="sm" />
                </span>
              </span>
            ) : null}
            {copied === null ? null : (
              <p role="status" className="ch-hours-copied">
                <Icon glyph={GLYPH.checkCircle} size="lg" />
                <span>
                  {copied.from}{portalText("operations", "s_hours_copied_to_337f6a32")}{String(copied.count)} {portalText("operations", "other_open_324ee3fd")}{" "}
                  {copied.count === 1 ? portalText("operations", "day_a2620cbc") : portalText("operations", "days")}{portalText("operations", "closed_days_stay_closed_f848a3ea")}</span>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setWeek(copied.previous);
                    setCopied(null);
                  }}
                >
                  {portalText("operations", "undo_39fc7212")}</Button>
              </p>
            )}
            <div className="ch-hours-days">
              {week.map((day, index) => {
                const name = DAY_NAME()[index] ?? "";
                return (
                  <div className="ch-hours-day" key={DAY_CODE[index]}>
                    <span className="ch-hours-day-label">{DAY_SHORT()[index]}</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={day.open}
                      aria-label={portalText("operations", "value_open_145d731e", { v0: String(name) })}
                      className="ch-hours-pill"
                      disabled={!canEdit}
                      onClick={() => {
                        setDay(index, (held) => ({ ...held, open: !held.open }));
                      }}
                    >
                      {day.open ? portalText("operations", "open_cf9b7706") : portalText("operations", "closed_88d86b77")}
                    </button>
                    <div className="ch-hours-shifts">
                      {day.open ? (
                        <>
                          {day.period.map((period, at) => (
                            <span className="ch-hours-shift" key={String(at)}>
                              <input
                                className="input"
                                type="text"
                                inputMode="numeric"
                                placeholder="HH:MM"
                                maxLength={5}
                                aria-label={portalText("operations", "value_opens_e80a8ed0", { v0: String(name) })}
                                value={period.opensAt}
                                disabled={!canEdit}
                                onChange={(event) => {
                                  const opensAt = event.currentTarget.value;
                                  setDay(index, (held) => ({
                                    ...held,
                                    period: held.period.map((one, each) => (each === at ? { ...one, opensAt } : one)),
                                  }));
                                }}
                              />
                              <span className="ch-hours-to">{portalText("operations", "to_4374aaee")}</span>
                              <input
                                className="input"
                                type="text"
                                inputMode="numeric"
                                placeholder="HH:MM"
                                maxLength={5}
                                aria-label={portalText("operations", "value_closes_f4784c48", { v0: String(name) })}
                                value={period.closesAt}
                                disabled={!canEdit}
                                onChange={(event) => {
                                  const closesAt = event.currentTarget.value;
                                  setDay(index, (held) => ({
                                    ...held,
                                    period: held.period.map((one, each) => (each === at ? { ...one, closesAt } : one)),
                                  }));
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-icon ch-hours-remove"
                                aria-label={portalText("operations", "remove_this_shift_66551c56")}
                                data-hidden={day.period.length > 1 ? undefined : "true"}
                                disabled={!canEdit || day.period.length < 2}
                                onClick={() => {
                                  setDay(index, (held) => ({ ...held, period: held.period.filter((_, each) => each !== at) }));
                                }}
                              >
                                <Icon glyph={GLYPH.x} size="sm" />
                              </button>
                            </span>
                          ))}
                          <span className="ch-hours-acts">
                            {day.period.length >= MAX_PERIOD_PER_DAY ? null : (
                              <Button
                                variant="ghost"
                                disabled={!canEdit}
                                onClick={() => {
                                  setDay(index, (held) => {
                                    const last = held.period.at(-1);
                                    return {
                                      ...held,
                                      period: [...held.period, { opensAt: last?.closesAt ?? "12:00", closesAt: "23:30" }],
                                    };
                                  });
                                }}
                              >
                                <Icon glyph={GLYPH.plus} size="xs" />
                                {portalText("operations", "add_hours_c4a82938")}</Button>
                            )}
                            {index === firstOpen ? (
                              <Button
                                variant="ghost"
                                aria-label={portalText("operations", "copy_value_hours_to_every_open_day_950d7298", { v0: String(name) })}
                                disabled={!canEdit}
                                onClick={() => {
                                  const previous = week;
                                  const count = week.filter((one, at) => at !== index && one.open).length;
                                  setSaved(null);
                                  setWeek(week.map((one, at) => (at !== index && one.open ? { ...one, period: day.period } : one)));
                                  setCopied({ from: name, count, previous });
                                }}
                              >
                                <Icon glyph={GLYPH.copy} size="xs" />
                                {portalText("operations", "copy_to_all_open_days_07abc1f4")}</Button>
                            ) : null}
                          </span>
                        </>
                      ) : (
                        <span className="ch-hours-closed">{portalText("operations", "closed_all_day_c09ef94b")}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <SpecialDateEditor
              title={portalText("operations", "special_dates_e6cbb291")}
              entry={specialDate}
              today={today}
              disabled={!canEdit}
              onEntry={(entry) => {
                touch();
                setEditedDate(entry);
              }}
            />
          </div>
        </div>

        <div className="ch-answering-pair">
          {answersAtAllHours ? null : (
          <div className="ch-card">
            <h3 className="ch-card-heading">{portalText("operations", "out_of_hours_44882236")}</h3>
            <AnsweringChoiceGroup
              name="afterHours"
              label={portalText("operations", "out_of_hours_44882236")}
              option={afterHoursOptionOf(choice?.forwardTo ?? null, forwardFact)}
              chosen={choice?.afterHoursChoice ?? null}
              forwardClosedReason={closedReason}
              disabled={!canEdit}
              onChoose={(afterHoursChoice) => {
                choose({ afterHoursChoice });
              }}
            />
            {choice === null ? <p className="ch-card-foot">{portalText("operations", "the_published_version_does_not_say_aa60caba")}</p> : null}
          </div>
          )}

          <div className="ch-card">
            <div className="ch-card-head">
              <h3 className="ch-card-heading ch-card-heading-flush">{portalText("operations", "when_every_line_is_busy_ff7e5ac2")}</h3>
              {busyCount === 0 ? null : (
                <Tag tone="accent-2">
                  {String(busyCount)} {busyCount === 1 ? portalText("operations", "caller_14dc69de") : portalText("operations", "callers_5bbff698")} {portalText("operations", "this_week_dae1e5f8")}</Tag>
              )}
            </div>
            <p className="ch-card-note">
              {busyCount === 0
                ? portalText("operations", "nobody_has_found_every_line_busy_in_the_last_seven_days_1283e939")
                : portalText("operations", "every_one_of_this_agent_s_valuelines_was_busy_for_value_va_e7443c08", { v0: String(voiceAgentCeiling === null ? "" : `${String(voiceAgentCeiling)} `), v1: String(String(busyCount)), v2: String(busyCount === 1 ? portalText("operations", "caller_14dc69de") : portalText("operations", "callers_5bbff698")), v3: String(overflowSentence(schedule.overflowLast7Day)) })}
              {currentCeiling === null ? "" : portalText("operations", "your_organization_allows_value_across_all_agents_402a9f63", { v0: String(String(currentCeiling)) })}
            </p>
            <AnsweringChoiceGroup
              name="overflow"
              label={portalText("operations", "when_every_line_is_busy_ff7e5ac2")}
              option={busyOptionOf(choice?.forwardTo ?? null, forwardFact)}
              chosen={choice?.busyChoice ?? null}
              forwardClosedReason={closedReason}
              disabled={!canEdit}
              onChoose={(busyChoice) => {
                choose({
                  busyChoice,
                  longestWaitMinute: busyChoice === "queue" ? (choice?.longestWaitMinute ?? DEFAULT_WAIT_MINUTE) : (choice?.longestWaitMinute ?? null),
                });
              }}
            />
            {choice?.busyChoice === "forward" ? (
              <p className="ch-card-foot">{portalText("operations", "website_voice_and_calls_through_your_own_sip_trunk_take_a_0ba62477")}</p>
            ) : null}
            {!queued ? null : (
              <LongestWaitField
                name="maxWait"
                minute={waitMinute}
                note={waitNoteOf(waitSecond)}
                disabled={!canEdit}
                onMinute={(longestWaitMinute) => {
                  choose({ longestWaitMinute });
                }}
              />
            )}
            {choice !== null && forwards(choice) && closedReason === null ? (
              <ForwardNumberField
                id="answering-forward-to"
                value={choice.forwardTo ?? ""}
                problem={forwardProblem}
                limitSentence={forwardLimitSentenceOf(forwardFact)}
                disabled={!canEdit}
                onValue={(forwardTo) => {
                  choose({ forwardTo });
                }}
              />
            ) : null}
            <div className="ch-card-actions">
              <Button
                variant="secondary"
                onClick={() => {
                  setQuotaRequestOpen(true);
                }}
              >
                {portalText("operations", "request_more_lines_cba2054b")}</Button>
              {busyCount === 0 ? null : (
                <Button variant="ghost" onClick={onSeeCallers}>
                  {portalText("operations", "see_the_fe5a99ac")}{String(busyCount)} {busyCount === 1 ? portalText("operations", "caller_14dc69de") : portalText("operations", "callers_5bbff698")}
                </Button>
              )}
              <PendingQuotaRequestLine />
            </div>
          </div>
        </div>

        {changed || saved !== null || save.isError ? (
          <div className="ch-hours-save">
            {changed ? (
              <Button
                variant="primary"
                disabled={problem !== null || save.isPending}
                onClick={() => {
                  save.mutate();
                }}
              >
                {save.isPending ? portalText("operations", "saving_56a2285c") : onlyWeek ? portalText("operations", "save_hours_as_a_new_version_520e5958") : portalText("operations", "save_changes_as_a_new_version_219e1b31")}
              </Button>
            ) : null}
            {changed ? (
              <Button variant="ghost" onClick={discard}>
                {portalText("operations", "discard_36fff63c")}</Button>
            ) : null}
            {problem === null ? null : (
              <span className="c-note c-note-critical" role="alert">
                {problem}
              </span>
            )}
            {saved === null ? null : (
              <span className="ch-note" role="status">
                {saved}
              </span>
            )}
            {save.isError ? (
              <span className="c-note c-note-critical" role="alert">
                {apiErrorText(save.error, portalText("operations", "the_changes_were_not_saved_nothing_changed_532fe584"))}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      <QuotaRequestDialog
        open={quotaRequestOpen}
        currentCeiling={currentCeiling}
        onClose={() => {
          setQuotaRequestOpen(false);
        }}
      />
    </>
  );
}
